import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { User, Building2, Mail, Phone, MapPin, StickyNote, FileText, CheckCircle, Calendar, Clock, ChevronUp } from 'lucide-react';
import { customersApi } from '../services/api';
import { useToast } from '../hooks/use-toast';

const CustomerDetailsInline = ({ customer, onClose }) => {
  const [notes, setNotes] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [newNoteText, setNewNoteText] = useState('');
  const [isLoadingNotes, setIsLoadingNotes] = useState(false);
  const [isLoadingQuotes, setIsLoadingQuotes] = useState(false);
  const [isSavingNote, setIsSavingNote] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (customer) {
      loadNotes();
      loadQuotes();
    }
  }, [customer]);

  const loadNotes = async () => {
    if (!customer?.id) return;
    
    setIsLoadingNotes(true);
    try {
      const response = await customersApi.getNotes(customer.id);
      setNotes(response.data || []);
    } catch (error) {
      console.error('Error loading notes:', error);
    } finally {
      setIsLoadingNotes(false);
    }
  };

  const loadQuotes = async () => {
    if (!customer?.id) return;
    
    setIsLoadingQuotes(true);
    try {
      const response = await customersApi.getQuotes(customer.id);
      setQuotes(response.data || []);
    } catch (error) {
      console.error('Error loading quotes:', error);
    } finally {
      setIsLoadingQuotes(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNoteText.trim()) {
      toast({
        title: "Hinweis",
        description: "Bitte geben Sie einen Text für die Notiz ein.",
        variant: "destructive",
      });
      return;
    }

    setIsSavingNote(true);
    try {
      await customersApi.addNote(customer.id, {
        text: newNoteText.trim(),
        createdBy: "system"
      });
      
      toast({
        title: "Notiz gespeichert ✅",
        description: "Die Notiz wurde erfolgreich hinzugefügt.",
      });
      
      setNewNoteText('');
      loadNotes();
    } catch (error) {
      toast({
        title: "Fehler",
        description: "Notiz konnte nicht gespeichert werden.",
        variant: "destructive",
      });
    } finally {
      setIsSavingNote(false);
    }
  };

  if (!customer) return null;

  return (
    <tr>
      <td colSpan="6" className="p-0">
        <div className="bg-slate-50 border-t-2 border-blue-200 p-6 space-y-6 animate-in slide-in-from-top">
          
          {/* Close Button */}
          <div className="flex justify-between items-center">
            <h3 className="text-xl font-bold text-slate-800 flex items-center gap-2">
              <User className="w-5 h-5 text-blue-600" />
              Details: {customer.company}
            </h3>
            <Button 
              variant="outline" 
              size="sm"
              onClick={onClose}
              className="flex items-center gap-2"
            >
              <ChevronUp className="w-4 h-4" />
              Schließen
            </Button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            
            {/* Left Column: Contact Info + Notes */}
            <div className="space-y-4">
              {/* Contact Info */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    Kontaktinformationen
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div className="flex items-start gap-2">
                    <Mail className="w-4 h-4 text-slate-500 mt-0.5" />
                    <span className="text-slate-700">{customer.email || '-'}</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Phone className="w-4 h-4 text-slate-500 mt-0.5" />
                    <span className="text-slate-700">{customer.phone || '-'}</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 text-slate-500 mt-0.5" />
                    <div>
                      <div className="text-slate-700">{customer.address || '-'}</div>
                      <div className="text-slate-700">{customer.city || '-'}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Notes */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <StickyNote className="w-4 h-4 text-yellow-600" />
                    Notizen ({notes.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {/* Add Note */}
                  <div className="mb-3 p-3 bg-yellow-50 border border-yellow-200 rounded">
                    <Textarea
                      value={newNoteText}
                      onChange={(e) => setNewNoteText(e.target.value)}
                      placeholder="Notiz eingeben..."
                      rows={2}
                      className="mb-2 text-sm"
                    />
                    <Button
                      onClick={handleAddNote}
                      disabled={isSavingNote || !newNoteText.trim()}
                      size="sm"
                      className="bg-yellow-600 hover:bg-yellow-700 text-white"
                    >
                      {isSavingNote ? 'Speichere...' : 'Speichern'}
                    </Button>
                  </div>

                  {/* Notes List */}
                  {isLoadingNotes ? (
                    <div className="text-center py-2 text-sm text-slate-500">Lade...</div>
                  ) : notes.length === 0 ? (
                    <div className="text-center py-4 text-sm text-slate-500">Noch keine Notizen</div>
                  ) : (
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {notes.map((note) => (
                        <div key={note.id} className="p-2 bg-white border border-slate-200 rounded text-xs">
                          <div className="flex items-center gap-1 text-slate-600 mb-1">
                            <Clock className="w-3 h-3" />
                            <span>{new Date(note.createdAt).toLocaleString('de-DE', {
                              dateStyle: 'short',
                              timeStyle: 'short'
                            })}</span>
                          </div>
                          <p className="text-slate-800">{note.text}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Right Column: Quotes */}
            <div>
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <FileText className="w-4 h-4 text-blue-600" />
                    Alle Angebote ({quotes.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {isLoadingQuotes ? (
                    <div className="text-center py-4 text-sm text-slate-500">Lade Angebote...</div>
                  ) : quotes.length === 0 ? (
                    <div className="text-center py-6 text-sm text-slate-500">
                      <FileText className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                      <p>Noch keine Angebote</p>
                    </div>
                  ) : (
                    <div className="max-h-96 overflow-y-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="text-xs">Nr.</TableHead>
                            <TableHead className="text-xs">Erstellt</TableHead>
                            <TableHead className="text-xs">Betrag</TableHead>
                            <TableHead className="text-xs">Status</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {quotes.map((quote) => {
                            const isAccepted = quote.acceptance || quote.acceptedAt;
                            const status = quote.status || 'Entwurf';
                            
                            return (
                              <TableRow key={quote.id}>
                                <TableCell className="text-xs font-medium">{quote.quoteNumber}</TableCell>
                                <TableCell className="text-xs">
                                  {new Date(quote.createdAt).toLocaleDateString('de-DE', { dateStyle: 'short' })}
                                </TableCell>
                                <TableCell className="text-xs font-semibold">
                                  {quote.total?.toLocaleString('de-DE', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2
                                  })}€
                                  {quote.monthlyTotal > 0 && (
                                    <div className="text-[10px] text-orange-600">
                                      +{quote.monthlyTotal?.toLocaleString('de-DE', {
                                        minimumFractionDigits: 2
                                      })}€/M
                                    </div>
                                  )}
                                </TableCell>
                                <TableCell>
                                  {isAccepted ? (
                                    <Badge className="bg-green-100 text-green-800 border-green-300 text-[10px] px-1 py-0">
                                      ✅
                                    </Badge>
                                  ) : (
                                    <Badge 
                                      variant="outline"
                                      className={`text-[10px] px-1 py-0 ${
                                        status === 'Entwurf' ? 'bg-slate-100 text-slate-700' :
                                        status === 'Versendet' ? 'bg-blue-100 text-blue-700' :
                                        'bg-yellow-100 text-yellow-700'
                                      }`}
                                    >
                                      {status.slice(0, 1)}
                                    </Badge>
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </td>
    </tr>
  );
};

export default CustomerDetailsInline;
